/**
 * Request Validation and Security Module
 * Validates API requests and implements security measures
 */

import { checkRateLimit, sanitizeQueryParams, sanitizeRequestBody, validateUrl } from './input-sanitizer.js';

/**
 * Security configuration
 */
const SECURITY_CONFIG = {
  maxRequestSize: 1024 * 1024, // 1MB
  maxQueryParams: 20,
  maxHeaderSize: 8192,
  allowedMethods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
  requiredHeaders: ['accept', 'content-type'],
  blockedHeaders: ['authorization', 'cookie', 'set-cookie'], // These shouldn't be used in public API
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  rateLimits: {
    GET: { maxRequests: 100, windowMs: 60000 }, // 100 requests per minute for GET
    POST: { maxRequests: 20, windowMs: 60000 },  // 20 requests per minute for POST
    PUT: { maxRequests: 10, windowMs: 60000 },   // 10 requests per minute for PUT
    DELETE: { maxRequests: 5, windowMs: 60000 }  // 5 requests per minute for DELETE
  }
};

/**
 * Validate HTTP method
 */
export function validateMethod(method) {
  if (!method || typeof method !== 'string') {
    throw new Error('Invalid HTTP method');
  }

  const upperMethod = method.toUpperCase();
  if (!SECURITY_CONFIG.allowedMethods.includes(upperMethod)) {
    throw new Error(`HTTP method ${method} is not allowed`);
  }

  return upperMethod;
}

/**
 * Validate and sanitize headers
 */
export function validateHeaders(headers = {}) {
  const validatedHeaders = {};
  let totalSize = 0;

  for (const [key, value] of Object.entries(headers)) {
    const lowerKey = key.toLowerCase();

    // Block dangerous headers
    if (SECURITY_CONFIG.blockedHeaders.includes(lowerKey)) {
      console.warn(`Blocked potentially dangerous header: ${key}`);
      continue;
    }

    // Validate header name and value
    if (typeof key !== 'string' || typeof value !== 'string') {
      continue;
    }

    // Check header size
    const headerSize = key.length + value.length;
    totalSize += headerSize;

    if (totalSize > SECURITY_CONFIG.maxHeaderSize) {
      throw new Error('Headers size exceeds maximum allowed');
    }

    // Sanitize header value
    const sanitizedValue = value
      .replace(/[\r\n]/g, '') // Remove CRLF injection
      .substring(0, 1000); // Limit header value length

    validatedHeaders[key] = sanitizedValue;
  }

  return validatedHeaders;
}

/**
 * Validate request URL
 */
export function validateRequestUrl(baseUrl, endpoint) {
  const fullUrl = `${baseUrl}${endpoint}`;

  if (!validateUrl(fullUrl)) {
    throw new Error('Invalid or potentially dangerous URL');
  }

  // Additional URL validation
  if (endpoint.includes('..') || endpoint.includes('//')) {
    throw new Error('Path traversal attempt detected');
  }

  return fullUrl;
}

/**
 * Validate request size
 */
export function validateRequestSize(body) {
  if (!body) return true;

  const size = JSON.stringify(body).length;
  if (size > SECURITY_CONFIG.maxRequestSize) {
    throw new Error(`Request body size (${size} bytes) exceeds maximum allowed (${SECURITY_CONFIG.maxRequestSize} bytes)`);
  }

  return true;
}

/**
 * Validate query parameters
 */
export function validateQueryParameters(params) {
  if (!params) return {};

  const paramCount = Object.keys(params).length;
  if (paramCount > SECURITY_CONFIG.maxQueryParams) {
    throw new Error(`Too many query parameters (${paramCount}). Maximum allowed: ${SECURITY_CONFIG.maxQueryParams}`);
  }

  return sanitizeQueryParams(params);
}

/**
 * Apply rate limiting based on method and endpoint
 */
export function applyRateLimit(method, endpoint) {
  const limits = SECURITY_CONFIG.rateLimits[method.toUpperCase()];
  if (limits) {
    checkRateLimit(
      `${method}:${endpoint}`,
      limits.maxRequests,
      limits.windowMs
    );
  }
}

/**
 * Generate request fingerprint for monitoring
 */
export function generateRequestFingerprint(method, endpoint, userAgent = '') {
  const timestamp = Math.floor(Date.now() / 60000); // Round to minute
  const fingerprint = `${method}:${endpoint}:${timestamp}`;

  return btoa(fingerprint).substring(0, 16); // Short hash for logging
}

/**
 * Comprehensive request validation
 */
export function validateRequest(method, endpoint, options = {}) {
  const { headers = {}, params = {}, body = null, baseUrl = '/api' } = options;

  try {
    // Validate method
    const validMethod = validateMethod(method);

    // Apply rate limiting
    applyRateLimit(validMethod, endpoint);

    // Validate URL
    const validUrl = validateRequestUrl(baseUrl, endpoint);

    // Validate headers
    const validHeaders = validateHeaders(headers);

    // Validate query parameters
    const validParams = validateQueryParameters(params);

    // Validate and sanitize body
    let validBody = null;
    if (body) {
      validateRequestSize(body);
      validBody = sanitizeRequestBody(body);
    }

    // Generate fingerprint for monitoring
    const fingerprint = generateRequestFingerprint(validMethod, endpoint, headers['user-agent']);

    return {
      method: validMethod,
      url: validUrl,
      headers: validHeaders,
      params: validParams,
      body: validBody,
      fingerprint,
      timestamp: new Date().toISOString()
    };

  } catch (error) {
    // Log security violation (in production, this should go to a security monitoring system)
    console.warn('Security validation failed:', {
      method,
      endpoint,
      error: error.message,
      timestamp: new Date().toISOString()
    });

    throw error;
  }
}

/**
 * Validate response to ensure it's safe
 */
export function validateResponse(response, data) {
  // Check for suspicious response patterns
  if (typeof data === 'string') {
    // Check for potential XSS in response
    const dangerousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /expression\s*\(/gi
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(data)) {
        console.warn('Potentially dangerous content detected in response');
        break;
      }
    }
  }

  // Validate response headers
  if (response.headers) {
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json') && !contentType.includes('text/')) {
      console.warn('Unexpected content type in response:', contentType);
    }
  }

  return data;
}

/**
 * Security middleware for request interception
 */
export function createSecurityMiddleware(options = {}) {
  const config = { ...SECURITY_CONFIG, ...options };

  return {
    beforeRequest: (method, endpoint, requestOptions) => {
      return validateRequest(method, endpoint, {
        ...requestOptions,
        baseUrl: config.baseUrl || '/api'
      });
    },

    afterResponse: (response, data) => {
      return validateResponse(response, data);
    },

    onError: (error) => {
      // Sanitize error messages to prevent information leakage
      if (error.message && error.message.includes('fetch')) {
        return new Error('Network request failed. Please try again.');
      }
      return error;
    }
  };
}